import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { PROPERTIES } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Bed, CalendarClock, Copy, Image as ImageIcon, Pencil, Upload, UserCheck, RefreshCw, BookOpen, ExternalLink, Check, X as XIcon, History, FileText, Eye, EyeOff, ShieldAlert, MapPin, Timer, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useSectionTheme } from "@/hooks/useSectionTheme";
import { AccessPointsEditor, LeaseEditor, UtilityAccountsEditor, ComplianceEditor } from "@/components/PropertyPodSections";
import { RecurringIncomeEditor, MeterUsagePanel } from "@/components/PropertyMeters";
import { PhotoRoomsEditor } from "@/components/DamageExtras";
import { StatementSection } from "@/components/StatementSection";
import { CleanerGuideGate } from "@/components/CleanerGuideGate";
import { DialogTour, DialogTourButton, keepDialogOpenForTour } from "@/components/PageTour";
import { Dialog as HistoryDialogRoot, DialogContent as HistoryDialogContent, DialogHeader as HistoryDialogHeader, DialogTitle as HistoryDialogTitle, DialogDescription as HistoryDialogDescription } from "@/components/ui/dialog";

const EMPTY = {
  name: "",
  address: "",
  bedrooms: 1,
  notes: "",
  hero_image_url: "",
  door_code: "",
  wifi_password: "",
  checkin_instructions: "",
  preferred_cleaner_id: "",
  cleaner_user_id: "",
  cleaning_company_id: "",
  cleaning_cost: "",
  ical_airbnb_url: "",
  ical_booking_url: "",
  ical_vrbo_url: "",
  ical_expedia_url: "",
  guestbook_enabled: true,
  guestbook_slug: "",
  guestbook_wifi_ssid: "",
  guestbook_checkout: "",
  guestbook_recommendations: "",
  guestbook_house_rules: "",
  guestbook_emergency_contact: "",
  direct_booking_enabled: false,
  direct_booking_nightly_rate: 0,
  direct_booking_min_nights: 2,
  direct_booking_cleaning_fee: 0,
  owner_name: "",
  owner_email: "",
  commission_overrides: {},
  lat: "",
  lng: "",
  access_points: [],
  lease: {},
  utility_accounts: [],
  recurring_income: [],
};

export default function Properties() {
  const sectionTheme = useSectionTheme("properties");
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isCleaner = user?.role === "cleaner";
  const [rows, setRows] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(null); // {property, history}
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [icalToken, setIcalToken] = useState("");
  const [uploading, setUploading] = useState(false);
  // Session Z49 — per-property bespoke PDF documents (guide + rental agreement).
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null); // "guide" | "agreement" | null
  const [cleaners, setCleaners] = useState([]);
  const [cleanerUsers, setCleanerUsers] = useState([]);
  const [cleaningCompanies, setCleaningCompanies] = useState([]);
  const [globalRates, setGlobalRates] = useState({});

  const CHANNEL_KEYS = [
    ["airbnb", "Airbnb"],
    ["booking", "Booking.com"],
    ["vrbo", "Vrbo"],
    ["expedia", "Expedia"],
    ["internal", "Direct"],
    ["hostaway", "Hostaway"],
  ];

  const uploadDocument = async (kind, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editing) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("PDF must be under 15 MB");
      return;
    }
    setUploadingDoc(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/properties/${editing}/documents?kind=${kind}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDocuments((d) => ({ ...d, [kind]: data.document }));
      toast.success(`${kind === "guide" ? "Property guide" : "Rental agreement"} uploaded`);
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploadingDoc(null);
    }
  };

  const removeDocument = async (kind) => {
    if (!editing) return;
    if (!window.confirm(`Remove the ${kind === "guide" ? "property guide" : "rental agreement"} PDF?`)) return;
    try {
      await api.delete(`/properties/${editing}/documents/${kind}`);
      setDocuments((d) => { const n = { ...d }; delete n[kind]; return n; });
      toast.success("Removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, hero_image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const load = () => api.get("/properties").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/ical/token").then((r) => setIcalToken(r.data.token));
    api.get("/contractors/cleaners").then((r) => setCleaners(r.data)).catch(() => {});
    api.get("/cleaner-users").then((r) => setCleanerUsers(r.data || [])).catch(() => {});
    api.get("/cleaning-companies").then((r) => setCleaningCompanies(r.data || [])).catch(() => {});
    api.get("/settings/commissions").then((r) => setGlobalRates(r.data?.rates || {})).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDocuments({});
    setOpen(true);
  };

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("edit");
    const hit = id && rows.find((r) => r.id === id);
    if (hit) { openEdit(hit); window.history.replaceState(null, "", "/properties"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const openEdit = (p) => {
    setEditing(p.id);
    api.get(`/properties/${p.id}/documents`)
      .then((r) => setDocuments(r.data?.documents || {}))
      .catch(() => setDocuments({}));
    setForm({
      name: p.name || "",
      address: p.address || "",
      bedrooms: p.bedrooms || 1,
      notes: p.notes || "",
      hero_image_url: p.hero_image_url || "",
      door_code: p.door_code || "",
      wifi_password: p.wifi_password || "",
      checkin_instructions: p.checkin_instructions || "",
      preferred_cleaner_id: p.preferred_cleaner_id || "",
      cleaner_user_id: p.cleaner_user_id || "",
      cleaning_company_id: p.cleaning_company_id || "",
      cleaning_cost: p.cleaning_cost ?? "",
      ical_airbnb_url: p.ical_airbnb_url || "",
      ical_booking_url: p.ical_booking_url || "",
      ical_vrbo_url: p.ical_vrbo_url || "",
      ical_expedia_url: p.ical_expedia_url || "",
      guestbook_enabled: p.guestbook_enabled !== false,
      guestbook_slug: p.guestbook_slug || "",
      guestbook_wifi_ssid: p.guestbook_wifi_ssid || "",
      guestbook_checkout: p.guestbook_checkout || "",
      guestbook_recommendations: p.guestbook_recommendations || "",
      guestbook_house_rules: p.guestbook_house_rules || "",
      guestbook_emergency_contact: p.guestbook_emergency_contact || "",
      direct_booking_enabled: !!p.direct_booking_enabled,
      direct_booking_nightly_rate: p.direct_booking_nightly_rate || 0,
      direct_booking_min_nights: p.direct_booking_min_nights || 2,
      direct_booking_cleaning_fee: p.direct_booking_cleaning_fee || 0,
      owner_name: p.owner_name || "",
      owner_email: p.owner_email || "",
      commission_overrides: p.commission_overrides || {},
      lat: p.lat ?? "",
      lng: p.lng ?? "",
      access_points: p.access_points || [],
      lease: p.lease || {},
      utility_accounts: p.utility_accounts || [],
      recurring_income: p.recurring_income || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      // Strip commission overrides where the value is blank so we fall back to the global rate
      const cleanOverrides = Object.fromEntries(
        Object.entries(form.commission_overrides || {}).filter(
          ([, v]) => v !== "" && v !== null && v !== undefined && !isNaN(Number(v)),
        ).map(([k, v]) => [k, Number(v)]),
      );
      const payload = {
        ...form,
        preferred_cleaner_id: form.preferred_cleaner_id || null,
        cleaner_user_id: form.cleaner_user_id || null,
        cleaning_company_id: form.cleaning_company_id || null,
        cleaning_cost: form.cleaning_cost === "" ? null : parseFloat(form.cleaning_cost),
        commission_overrides: cleanOverrides,
        lat: form.lat === "" || form.lat === null || form.lat === undefined ? null : parseFloat(form.lat),
        lng: form.lng === "" || form.lng === null || form.lng === undefined ? null : parseFloat(form.lng),
      };
      if (editing) {
        await api.put(`/properties/${editing}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property added");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    load();
  };

  const icalUrl = (propertyId) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!icalToken) return "";
    return propertyId
      ? `${base}/api/ical/property/${propertyId}.ics?token=${icalToken}`
      : `${base}/api/ical/all.ics?token=${icalToken}`;
  };

  const copyIcal = (propertyId, name) => {
    const url = icalUrl(propertyId);
    navigator.clipboard.writeText(url);
    toast.success(`iCal link for ${name} copied — share with cleaners`);
  };

  const [syncing, setSyncing] = useState(false);
  const syncIcalFeeds = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/ical/sync-now");
      toast.success(
        `Airbnb/Booking sync complete · ${data.new || 0} new, ${data.updated || 0} updated`,
      );
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const copyGuestBook = (p) => {
    if (!p.guestbook_slug) {
      toast.error("Save the property first to generate a guest book link");
      return;
    }
    const url = `${window.location.origin}/g/${p.guestbook_slug}`;
    navigator.clipboard.writeText(url);
    toast.success(`Guest book link for ${p.name} copied`);
  };

  const openGuestBook = (p) => {
    if (!p.guestbook_slug) return;
    window.open(`/g/${p.guestbook_slug}`, "_blank", "noopener");
  };

  const approvePendingCost = async (p) => {
    try {
      await api.post(`/properties/${p.id}/cleaning-cost/approve`);
      toast.success(`£${Number(p.pending_cleaning_cost).toFixed(2)} approved for ${p.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    }
  };

  const rejectPendingCost = async (p) => {
    try {
      await api.post(`/properties/${p.id}/cleaning-cost/reject`);
      toast.success(`Change rejected for ${p.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || apiError(err));
    }
  };

  const openCostHistory = async (p) => {
    try {
      const r = await api.get(`/properties/${p.id}/cleaning-cost-history`);
      setHistoryOpen({ property: p, ...r.data });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const [impactPreviews, setImpactPreviews] = useState({}); // {propertyId: impactData}

  // Session Z85 — property access audit. Cleaners click "Reveal access" to
  // uncover the door code / Wi-Fi; owners get a global log modal.
  // Session Z87 — reveal auto-blurs again after 60 seconds and we expose
  // a one-tap "I'm here" shortcut that bumps today's turnover to in_progress.
  const [revealed, setRevealed] = useState({}); // {propertyId: expiresAtMs}
  const [tick, setTick] = useState(0); // forces countdown re-render
  const [arrivalBusy, setArrivalBusy] = useState({}); // {propertyId: true}
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const REVEAL_WINDOW_MS = 60 * 1000;

  useEffect(() => {
    if (Object.keys(revealed).length === 0) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [revealed]);

  const revealAccess = async (p) => {
    const expires = Date.now() + REVEAL_WINDOW_MS;
    try {
      await api.post(`/properties/${p.id}/access-log`, { reason: "reveal_access" });
      setRevealed((prev) => ({ ...prev, [p.id]: expires }));
    } catch (err) {
      setRevealed((prev) => ({ ...prev, [p.id]: expires }));
      toast.error(apiError(err) || "Access logged locally only");
    }
    // Auto-blur after 60s so a borrowed phone can't photograph it later.
    setTimeout(() => {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
    }, REVEAL_WINDOW_MS);
  };

  const secondsLeft = (p) => {
    const exp = revealed[p.id];
    if (!exp) return 0;
    return Math.max(0, Math.ceil((exp - Date.now()) / 1000));
  };

  const markImHere = async (p) => {
    setArrivalBusy((prev) => ({ ...prev, [p.id]: true }));
    // Session Z88 — opt-in geolocation. If the user approves the browser
    // prompt we attach lat/lng/accuracy so the audit log proves on-site.
    // If they deny or the API isn't available we still fire the request so
    // the arrival still gets logged.
    const getGeo = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
      );
    });
    try {
      const geo = await getGeo();
      const { data } = await api.post(`/properties/${p.id}/im-here`, geo || {});
      const geoNote = data.geo_captured ? " · GPS pinned" : "";
      if (data.task_bumped) {
        toast.success(`${p.name} marked in progress${geoNote}`);
      } else if (data.task_status) {
        toast.message(`Already in progress — arrival logged${geoNote}`);
      } else {
        toast.message(`Arrival logged (no turnover today)${geoNote}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setArrivalBusy((prev) => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await api.get("/property-access-log?limit=100");
      setAuditRows(r.data || []);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAuditLoading(false);
    }
  };

  // Session Z94 — one-click geocode backfill for every property with a
  // non-blank address but null lat/lng. Reloads the list on success so the
  // "GPS" indicator on each card flips to populated.
  // Session Z95 — now streams per-property results via SSE so the user
  // sees live progress rather than a single final toast.
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);
  // {total, index, filled, skipped, needs_review, current}

  const runGeocodeBackfill = async () => {
    if (!isOwner) return;
    setBackfillBusy(true);
    setBackfillProgress({ total: 0, index: 0, filled: 0, skipped: 0, needs_review: 0, current: "" });
    try {
      const token = window.localStorage.getItem("token") || window.localStorage.getItem("ns_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/geocode-backfill/stream`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // Local accumulators — avoid closing over stale state inside the loop.
      let totals = { filled: 0, skipped: 0, needs_review: 0, total: 0 };
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === "start") {
            totals.total = evt.candidates;
            setBackfillProgress({ total: evt.candidates, index: 0, filled: 0, skipped: 0, needs_review: 0, current: "" });
          } else if (evt.type === "progress") {
            if (evt.status === "filled") {
              totals.filled += 1;
              if ((evt.validation || {}).status === "needs_review") totals.needs_review += 1;
            } else {
              totals.skipped += 1;
            }
            setBackfillProgress({
              total: evt.total,
              index: evt.index,
              filled: totals.filled,
              skipped: totals.skipped,
              needs_review: totals.needs_review,
              current: evt.property_name || "",
            });
          } else if (evt.type === "done") {
            if (evt.candidates === 0) {
              toast.message("No properties missing coordinates");
            } else if (evt.filled === 0) {
              toast.warning(`Checked ${evt.candidates} — Nominatim couldn't resolve any`);
            } else {
              const reviewSuffix = evt.needs_review > 0 ? ` · ${evt.needs_review} need review` : "";
              toast.success(`Geocoded ${evt.filled} of ${evt.candidates}${reviewSuffix}`);
            }
          }
        }
      }
      await load();
    } catch (err) {
      toast.error(apiError(err) || "Backfill stream failed");
    } finally {
      setBackfillBusy(false);
      // Keep the summary visible for a beat so the final counts don't vanish.
      setTimeout(() => setBackfillProgress(null), 4000);
    }
  };

  const loadImpact = async (p) => {
    if (impactPreviews[p.id]) return;
    try {
      const r = await api.get(`/properties/${p.id}/cleaning-cost-impact`);
      setImpactPreviews((prev) => ({ ...prev, [p.id]: r.data }));
    } catch { /* silent — impact preview is best-effort */ }
  };

  useEffect(() => {
    // Prime impact for any property with a pending cost so the badge is
    // rendered with context on first paint.
    rows.filter((p) => p.pending_cleaning_cost !== null && p.pending_cleaning_cost !== undefined)
      .forEach((p) => loadImpact(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <Layout
      title="Properties"
      subtitle="Your serviced homes. Add, rename or retire units as your portfolio grows."
      actions={
        isCleaner ? (
          <Button
            variant="outline"
            onClick={() => copyIcal(null, "All bookings")}
            className="rounded-full h-11 px-5"
          >
            <CalendarClock className="w-4 h-4 mr-2" /> Cleaners&apos; calendar (all)
          </Button>
        ) : (
          <>
            <Button
              data-testid="properties-audit-btn"
              variant="outline"
              onClick={openAudit}
              className="rounded-full h-11 px-5"
              title="Who revealed door codes / Wi-Fi — audit trail"
            >
              <ShieldAlert className="w-4 h-4 mr-2" /> Access log
            </Button>
            {isOwner && (
              <Button
                data-testid="properties-geocode-backfill-btn"
                variant="outline"
                onClick={runGeocodeBackfill}
                disabled={backfillBusy}
                className="rounded-full h-11 px-5"
                title="Fill lat/lng on every property with a blank address — uses OpenStreetMap"
              >
                <Globe2 className={`w-4 h-4 mr-2 ${backfillBusy ? "animate-spin" : ""}`} />
                {backfillBusy ? "Geocoding…" : "Fill coords"}
              </Button>
            )}
            <Button
              data-testid={PROPERTIES.icalSyncNowBtn}
              variant="outline"
              onClick={syncIcalFeeds}
              disabled={syncing}
              className="rounded-full h-11 px-5"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Airbnb/Booking"}
            </Button>
            <Button
              variant="outline"
              onClick={() => copyIcal(null, "All bookings")}
              className="rounded-full h-11 px-5"
            >
              <CalendarClock className="w-4 h-4 mr-2" /> Cleaners&apos; calendar (all)
            </Button>
            <Button
              data-testid={PROPERTIES.addBtn}
              onClick={openNew}
              className="rounded-full ns-btn-primary h-11 px-6"
            >
              <Plus className="w-4 h-4 mr-2" /> Add property
            </Button>
          </>
        )
      }
    >
      {isCleaner && <div className="mb-4"><CleanerGuideGate /></div>}
      {backfillProgress && (
        <div
          data-testid="properties-backfill-progress"
          className="fixed bottom-6 right-6 z-50 w-80 rounded-2xl border border-amber-200 bg-white shadow-lg p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-slate-900">Geocoding properties</div>
            <div className="text-[11px] text-slate-500">
              {backfillProgress.index}/{backfillProgress.total || "?"}
            </div>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-2">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{
                width: backfillProgress.total
                  ? `${Math.min(100, (backfillProgress.index / backfillProgress.total) * 100)}%`
                  : "0%",
              }}
            />
          </div>
          {backfillProgress.current && (
            <div className="text-[11px] text-slate-500 truncate" title={backfillProgress.current}>
              {backfillProgress.current}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              {backfillProgress.filled} filled
            </span>
            <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-100">
              {backfillProgress.skipped} skipped
            </span>
            {backfillProgress.needs_review > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                {backfillProgress.needs_review} need review
              </span>
            )}
          </div>
        </div>
      )}
      <section className={`ns-section ns-section-${sectionTheme} mb-6`} data-section-theme={sectionTheme}>
        <div className="flex items-start gap-3">
          <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
          <div className="flex-1 min-w-0">
            <div className="ns-label text-teal-800">Portfolio</div>
            <div className="font-serif text-lg text-slate-900 mt-0.5">Serviced units in rotation</div>
            <div className="text-xs text-slate-500 mt-1">
              Each card is a live unit — tap to edit pricing, upload a hero image, or retire the unit.
            </div>
          </div>
        </div>
      </section>

      <div data-testid={PROPERTIES.root} className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {rows.map((p) => (
          <div key={p.id} className="ns-card overflow-hidden flex flex-col group">
            <div
              className="h-40 bg-slate-100 relative"
              style={
                p.hero_image_url
                  ? {
                      backgroundImage: `url(${p.hero_image_url})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : undefined
              }
            >
              {!p.hero_image_url && (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-4 text-white">
                <div className="text-[10px] tracking-[0.24em] uppercase opacity-80">Unit</div>
                <div className="font-serif text-2xl leading-tight drop-shadow">{p.name}</div>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="text-sm text-slate-500">{p.address || "Newport, Wales"}</div>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1">
                  <Bed className="w-4 h-4 text-teal-700" /> {p.bedrooms} bed
                </span>
                {p.preferred_cleaner_id && (
                  <span className="inline-flex items-center gap-1 text-teal-700 text-xs">
                    <UserCheck className="w-3.5 h-3.5" />
                    {cleaners.find((c) => c.id === p.preferred_cleaner_id)?.first_name || "Cleaner"} pinned
                  </span>
                )}
              </div>
              {p.notes && (
                <p className="text-xs text-slate-500 mt-3 line-clamp-2">{p.notes}</p>
              )}
              {!isCleaner && p.address_validation?.status === "needs_review" && (
                <div
                  data-testid={`properties-needs-review-${p.id}`}
                  className="mt-2 inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-100 rounded-full px-2 py-0.5"
                  title={`Geocoded coords are ${Math.round(p.address_validation.postcode_distance_m)} m from the ${p.address_validation.postcode} centroid — likely wrong city or a typo in the address.`}
                >
                  <ShieldAlert className="w-3 h-3" /> Address needs review · {p.address_validation.postcode}
                </div>
              )}
              {isCleaner && (p.door_code || p.wifi_password || p.checkin_instructions) && (
                <div
                  data-testid={`properties-cleaner-access-${p.id}`}
                  className="mt-3 rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-xs space-y-1"
                ><div className="flex items-center justify-between gap-2">
                    <div className="uppercase tracking-wider text-[9px] text-amber-700 font-medium">
                      Turnover access
                    </div>
                    {!revealed[p.id] && (
                      <button
                        type="button"
                        onClick={() => revealAccess(p)}
                        data-testid={`properties-reveal-access-${p.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-700 text-white px-2.5 py-1 text-[10px] hover:bg-amber-800 transition"
                        title="Reveal door code & Wi-Fi — blurs again in 60s and logs you"
                      >
                        <Eye className="w-3 h-3" /> Reveal access
                      </button>
                    )}
                    {revealed[p.id] && (
                      <span
                        data-testid={`properties-access-revealed-${p.id}`}
                        className="inline-flex items-center gap-1 text-[10px] text-amber-700"
                      >
                        <Timer className="w-3 h-3" /> {secondsLeft(p)}s
                      </span>
                    )}
                  </div>
                  {!revealed[p.id] ? (
                    <div className="text-slate-500 italic flex items-center gap-1 mt-1">
                      <EyeOff className="w-3 h-3" /> Door code &amp; Wi-Fi hidden — reveal auto-blurs in 60s
                    </div>
                  ) : (
                    <>
                      {p.door_code && (
                        <div><span className="text-slate-500">Door code:</span> <span className="font-mono font-medium text-slate-800">{p.door_code}</span></div>
                      )}
                      {(p.access_points || []).filter((a) => a.code).map((a) => (
                        <div key={a.id} data-testid={`properties-cleaner-access-point-${a.id}`}>
                          <span className="text-slate-500">{a.label || a.type}:</span> <span className="font-mono font-medium text-slate-800">{a.code}</span>
                          {a.location_note && <span className="text-slate-500"> · {a.location_note}</span>}
                        </div>
                      ))}
                      {p.wifi_password && (
                        <div><span className="text-slate-500">Wi-Fi:</span> <span className="font-mono text-slate-800">{p.wifi_password}</span></div>
                      )}
                      {p.checkin_instructions && (
                        <div className="text-slate-600 line-clamp-3">{p.checkin_instructions}</div>
                      )}
                    </>
                  )}
                  <div className="pt-1.5 mt-1 border-t border-amber-100">
                    <button
                      type="button"
                      onClick={() => markImHere(p)}
                      disabled={!!arrivalBusy[p.id]}
                      data-testid={`properties-im-here-${p.id}`}
                      className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-teal-700 text-white px-3 py-1.5 text-[11px] font-medium hover:bg-teal-800 transition disabled:opacity-60"
                      title="Mark today's turnover in-progress and timestamp the audit log"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      {arrivalBusy[p.id] ? "Logging…" : "I'm here"}
                    </button>
                  </div>
                </div>
              )}
              {p.pending_cleaning_cost !== null && p.pending_cleaning_cost !== undefined && (
                <div
                  data-testid={`properties-pending-cost-${p.id}`}
                  className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="uppercase tracking-wider text-[9px] text-amber-700 font-medium">
                        Pending cleaning cost
                      </div>
                      <div className="text-slate-800 mt-0.5">
                        <span className="line-through text-slate-400 mr-1.5">
                          £{Number(p.cleaning_cost || 0).toFixed(2)}
                        </span>
                        <span className="font-semibold">
                          £{Number(p.pending_cleaning_cost).toFixed(2)}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                        Proposed by {p.pending_cleaning_cost_by_name || "team"}
                        {p.pending_cleaning_cost_at
                          ? ` · ${new Date(p.pending_cleaning_cost_at).toLocaleDateString()}`
                          : ""}
                      </div>
                      {impactPreviews[p.id] && impactPreviews[p.id].total_delta_next_month !== null && (
                        <div
                          data-testid={`properties-pending-cost-impact-${p.id}`}
                          className={`text-[11px] mt-1.5 font-medium ${
                            impactPreviews[p.id].total_delta_next_month > 0
                              ? "text-rose-700"
                              : impactPreviews[p.id].total_delta_next_month < 0
                                ? "text-emerald-700"
                                : "text-slate-500"
                          }`}
                        >
                          Next-month payout impact:{" "}
                          {impactPreviews[p.id].total_delta_next_month > 0 ? "−" :
                           impactPreviews[p.id].total_delta_next_month < 0 ? "+" : ""}
                          £{Math.abs(impactPreviews[p.id].total_delta_next_month).toFixed(2)}
                          <span className="text-slate-400 font-normal ml-1">
                            ({impactPreviews[p.id].next_month_turnovers} turnover{impactPreviews[p.id].next_month_turnovers === 1 ? "" : "s"} · £{Math.abs(impactPreviews[p.id].delta_per_turnover).toFixed(2)} each)
                          </span>
                        </div>
                      )}
                    </div>
                    {isOwner ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => approvePendingCost(p)}
                          data-testid={`properties-pending-cost-approve-${p.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[10px] hover:bg-emerald-700 transition"
                          title="Apply this new cost to monthly reports"
                        >
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectPendingCost(p)}
                          data-testid={`properties-pending-cost-reject-${p.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-600 px-2.5 py-1 text-[10px] hover:bg-stone-50 transition"
                          title="Discard this proposal"
                        >
                          <XIcon className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    ) : (
                      <span
                        data-testid={`properties-pending-cost-awaiting-${p.id}`}
                        className="text-[10px] text-amber-700 italic shrink-0"
                      >
                        Awaiting owner approval
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-auto pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => copyIcal(p.id, p.name)}
                    className="inline-flex items-center gap-1.5 text-xs text-teal-700 hover:underline"
                    title="Copy iCal subscription URL"
                  >
                    <Copy className="w-3.5 h-3.5" /> Cleaners&apos; iCal
                  </button>
                  {p.guestbook_slug && p.guestbook_enabled !== false && (
                    <button
                      data-testid={PROPERTIES.guestBookLinkBtn}
                      onClick={() => copyGuestBook(p)}
                      className="inline-flex items-center gap-1.5 text-xs text-teal-700 hover:underline"
                      title="Copy shareable guest book link"
                    >
                      <BookOpen className="w-3.5 h-3.5" /> Guest book
                    </button>
                  )}
                  {p.guestbook_slug && p.guestbook_enabled !== false && (
                    <button
                      onClick={() => openGuestBook(p)}
                      className="text-slate-300 hover:text-teal-700"
                      title="Open guest book in new tab"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {!isCleaner && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openCostHistory(p)}
                      data-testid={`properties-cost-history-${p.id}`}
                      className="text-slate-300 hover:text-indigo-700 p-1.5"
                      title="Cleaning cost history"
                    >
                      <History className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEdit(p)}
                      className="text-slate-300 hover:text-teal-700 p-1.5"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(p.id)}
                      className="text-slate-300 hover:text-red-600 p-1.5"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <HistoryDialogRoot open={!!historyOpen} onOpenChange={(v) => !v && setHistoryOpen(null)}>
        <HistoryDialogContent className="max-w-2xl" data-testid="properties-cost-history-dialog">
          <HistoryDialogHeader>
            <HistoryDialogTitle>{historyOpen?.property?.name} · Cleaning cost history</HistoryDialogTitle>
            <HistoryDialogDescription>
              Every proposal, approval and rejection — newest first.
            </HistoryDialogDescription>
          </HistoryDialogHeader>
          {historyOpen && (historyOpen.history || []).length > 0 && (
            <div className="flex justify-end mb-1 gap-2">
              <a
                href={`/api/properties/${historyOpen.property.id}/cleaning-cost-history.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="properties-cost-history-pdf"
                className="text-[11px] rounded-full bg-teal-700 text-white px-3 py-1.5 hover:bg-teal-800 transition inline-flex items-center gap-1.5"
              >
                📄 Download PDF
              </a>
              <a
                href={`/api/properties/${historyOpen.property.id}/cleaning-cost-history.csv`}
                target="_blank"
                rel="noreferrer"
                data-testid="properties-cost-history-csv"
                className="text-[11px] rounded-full bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-800 transition inline-flex items-center gap-1.5"
              >
                ⬇ Download CSV
              </a>
            </div>
          )}
          <div className="max-h-[440px] overflow-y-auto space-y-2 mt-2">
            {(historyOpen?.history || []).length === 0 ? (
              <div
                data-testid="properties-cost-history-empty"
                className="text-sm text-slate-400 italic p-6 text-center rounded-xl border border-dashed border-slate-200"
              >
                No cost changes yet — edit the cleaning cost to seed the audit trail.
              </div>
            ) : (
              (historyOpen?.history || []).map((h) => {
                const palette = h.action === "approved"
                  ? { pill: "bg-emerald-100 text-emerald-800", border: "border-emerald-100 bg-emerald-50/40", label: "Approved" }
                  : h.action === "rejected"
                    ? { pill: "bg-rose-100 text-rose-800", border: "border-rose-100 bg-rose-50/40", label: "Rejected" }
                    : h.action === "direct_edit"
                      ? { pill: "bg-slate-100 text-slate-800", border: "border-slate-100 bg-white", label: "Direct edit" }
                      : { pill: "bg-amber-100 text-amber-800", border: "border-amber-100 bg-amber-50/40", label: "Proposed" };
                return (
                  <div
                    key={h.id}
                    data-testid={`properties-cost-history-row-${h.id}`}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${palette.border}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${palette.pill}`}>
                          {palette.label}
                        </span>
                        <span className="text-slate-700 font-medium">
                          {h.from_value === null || h.from_value === undefined
                            ? "not set"
                            : `£${Number(h.from_value).toFixed(2)}`}
                          <span className="text-slate-300 mx-1.5">→</span>
                          {h.to_value === null || h.to_value === undefined
                            ? "not set"
                            : `£${Number(h.to_value).toFixed(2)}`}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 truncate">
                        by {h.actor_name || "—"}
                        {h.proposer_name && h.proposer_name !== h.actor_name && (
                          <span className="text-slate-400"> · proposed by {h.proposer_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-400 whitespace-nowrap">
                      {new Date(h.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </HistoryDialogContent>
    </HistoryDialogRoot>

    <HistoryDialogRoot open={auditOpen} onOpenChange={setAuditOpen}>
      <HistoryDialogContent className="max-w-3xl" data-testid="properties-audit-dialog">
        <HistoryDialogHeader>
          <HistoryDialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" /> Property access log
          </HistoryDialogTitle>
          <HistoryDialogDescription>
            Every time a team-mate reveals door codes or Wi-Fi, we drop a row here. Newest first — limit 100.
          </HistoryDialogDescription>
        </HistoryDialogHeader>
        <div className="max-h-[520px] overflow-y-auto mt-2">
          {auditLoading ? (
            <div className="text-sm text-slate-400 italic p-6 text-center">Loading…</div>
          ) : auditRows.length === 0 ? (
            <div
              data-testid="properties-audit-empty"
              className="text-sm text-slate-400 italic p-6 text-center rounded-xl border border-dashed border-slate-200"
            >
              No access events yet — cleaners haven&apos;t revealed any door codes.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Who</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Property</th>
                  <th className="py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`properties-audit-row-${row.id}`}
                    className="border-b border-slate-50 hover:bg-amber-50/30"
                  >
                    <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(row.viewed_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 text-slate-800">
                      <div className="font-medium text-xs">{row.user_name}</div>
                      <div className="text-[11px] text-slate-400">{row.user_email}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] capitalize bg-amber-50 text-amber-800 border border-amber-100">
                        {row.user_role}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-700">{row.property_name}</td>
                    <td className="py-2 text-[11px] text-slate-500">{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </HistoryDialogContent>
    </HistoryDialogRoot>


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={keepDialogOpenForTour}>
          <DialogTour open={open} tourKey="/properties#edit" />
          <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit property" : "New property"}
            </DialogTitle>
            <DialogTourButton tourKey="/properties#edit" />
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="ns-label">Name</Label>
              <Input
                data-testid={PROPERTIES.nameInput}
                className="mt-2 h-11 bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Hero photo</Label>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-200 bg-white cursor-pointer hover:border-teal-600 hover:text-teal-700 text-sm text-slate-700">
                  <Upload className="w-4 h-4" />
                  {uploading
                    ? "Uploading…"
                    : form.hero_image_url
                      ? "Replace photo"
                      : "Upload photo"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={uploading}
                    onChange={uploadPhoto}
                  />
                </label>
                {form.hero_image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, hero_image_url: "" })}
                    className="text-xs text-slate-500 hover:text-red-600 underline"
                  >
                    Remove photo
                  </button>
                )}
              </div>
              <Input
                className="mt-2 h-11 bg-white"
                placeholder="…or paste an image URL"
                value={form.hero_image_url}
                onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
              />
              {form.hero_image_url && (
                <img
                  src={form.hero_image_url}
                  alt=""
                  className="mt-2 w-full h-32 object-cover rounded-lg border border-slate-100"
                />
              )}
            </div>
            <div>
              <Label className="ns-label">Address</Label>
              <Input
                data-testid={PROPERTIES.addressInput}
                className="mt-2 h-11 bg-white"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Bedrooms</Label>
              <Input
                data-testid={PROPERTIES.bedroomsInput}
                type="number"
                min={1}
                className="mt-2 h-11 bg-white"
                value={form.bedrooms}
                onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Door code</Label>
                <Input
                  data-testid="properties-door-code"
                  className="mt-2 h-11 bg-white font-mono"
                  placeholder="1234#"
                  value={form.door_code}
                  onChange={(e) => setForm({ ...form, door_code: e.target.value })}
                />
              </div>
              <div>
                <Label className="ns-label">Wi-Fi password</Label>
                <Input
                  data-testid="properties-wifi-password"
                  className="mt-2 h-11 bg-white font-mono"
                  placeholder="EstuaryBreeze24"
                  value={form.wifi_password}
                  onChange={(e) => setForm({ ...form, wifi_password: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="ns-label">Check-in SMS instructions</Label>
              <Textarea
                data-testid="properties-checkin-instructions"
                className="mt-2 bg-white"
                rows={2}
                placeholder="Front door on Alma Terrace; kitchen light on for evening arrivals."
                value={form.checkin_instructions}
                onChange={(e) => setForm({ ...form, checkin_instructions: e.target.value })}
              />
              <div className="text-[11px] text-slate-400 mt-1.5">
                Included in the automated 8:15am check-in SMS on arrival day.
              </div>
            </div>
            <div data-testid="properties-access-section"><AccessPointsEditor value={form.access_points} onChange={(v) => setForm({ ...form, access_points: v })} /></div>
            <div data-testid="properties-lease-section"><LeaseEditor value={form.lease} onChange={(v) => setForm({ ...form, lease: v })} /></div>
            <UtilityAccountsEditor value={form.utility_accounts} onChange={(v) => setForm({ ...form, utility_accounts: v })} />
            <RecurringIncomeEditor value={form.recurring_income} onChange={(v) => setForm({ ...form, recurring_income: v })} />
            <PhotoRoomsEditor propertyId={editing} canEdit={!isCleaner} />
            <ComplianceEditor propertyId={editing} />
            <MeterUsagePanel propertyId={editing} />
            <StatementSection propertyId={editing} ownerEmail={form.owner_email} />
            <div>
              <Label className="ns-label">Cleaning company</Label>
              <select
                data-testid="properties-cleaning-company"
                className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={form.cleaning_company_id || ""}
                onChange={(e) => setForm({ ...form, cleaning_company_id: e.target.value })}
              >
                <option value="">— No cleaning company —</option>
                {cleaningCompanies.filter((c) => c.active !== false || c.id === form.cleaning_company_id).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.contact_name ? ` · ${c.contact_name}` : ""}
                    {c.active === false ? " (inactive)" : ""}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-slate-400 mt-1.5">
                Manage the list in <span className="italic">Settings → Cleaning companies</span>.
              </div>
            </div>
            <div>
              <Label className="ns-label">Cleaning cost (£ per turnover)</Label>
              <Input
                data-testid="properties-cleaning-cost"
                type="number"
                step="0.01"
                min="0"
                className="mt-2"
                value={form.cleaning_cost}
                onChange={(e) => setForm({ ...form, cleaning_cost: e.target.value })}
                placeholder="e.g. 45.00"
              />
              <div className="text-[11px] text-slate-400 mt-1.5">
                Stamped on every new turnover task and included in the landlord monthly report.
              </div>
            </div>
            <div>
              <Label className="ns-label">Preferred cleaner</Label>
              <select
                data-testid="properties-preferred-cleaner"
                className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={form.preferred_cleaner_id || ""}
                onChange={(e) => setForm({ ...form, preferred_cleaner_id: e.target.value })}
              >
                <option value="">Auto-pick highest-rated cleaner</option>
                {cleaners.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                    {c.rating ? ` · ${c.rating}★` : ""}
                    {c.phone ? "" : " (no phone — will be skipped)"}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-slate-400 mt-1.5">
                Pinned cleaner gets the 8:10am turnover SMS on checkout days for this unit.
              </div>
            </div>
            <div>
              <Label className="ns-label">Cleaner login (auto-assigns cleaning tasks)</Label>
              <select
                data-testid="properties-cleaner-user"
                className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={form.cleaner_user_id || ""}
                onChange={(e) => setForm({ ...form, cleaner_user_id: e.target.value })}
              >
                <option value="">— No cleaner login pinned —</option>
                {cleanerUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}{u.phone ? "" : " (no phone — skipped in 07:00 SMS digest)"}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-slate-400 mt-1.5">
                New cleaning tasks for this property are auto-assigned to this cleaner. They also receive the daily 07:00 UTC turnover SMS digest.
              </div>
            </div>
            <div>
              <Label className="ns-label">Notes</Label>
              <Textarea
                className="mt-2 bg-white"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="ns-label mb-2">Commission overrides (this unit)</div>
              <div className="text-[11px] text-slate-400 mb-3">
                Leave blank to use the global rate. Enter a % to override just this
                property (e.g. Airbnb Superhost at 2%).
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CHANNEL_KEYS.map(([key, label]) => {
                  const globalPct = globalRates[key];
                  const val = form.commission_overrides?.[key];
                  return (
                    <div key={key}>
                      <Label className="text-xs text-slate-500 flex items-center justify-between">
                        <span>{label}</span>
                        <span className="text-[10px] text-slate-400">
                          global {globalPct !== undefined ? `${globalPct}%` : "—"}
                        </span>
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          data-testid={`properties-commission-${key}`}
                          type="number"
                          min={0}
                          step="0.1"
                          placeholder={globalPct !== undefined ? String(globalPct) : ""}
                          className="h-10 bg-white pr-8"
                          value={val ?? ""}
                          onChange={(e) => {
                            const next = { ...(form.commission_overrides || {}) };
                            if (e.target.value === "") delete next[key];
                            else next[key] = e.target.value;
                            setForm({ ...form, commission_overrides: next });
                          }}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="ns-label mb-2">Property owner (landlord)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Owner name</Label>
                  <Input
                    data-testid="properties-owner-name"
                    className="mt-1 h-10 bg-white"
                    placeholder="Jane Smith"
                    value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Owner email</Label>
                  <Input
                    data-testid="properties-owner-email"
                    type="email"
                    className="mt-1 h-10 bg-white"
                    placeholder="jane@example.com"
                    value={form.owner_email}
                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2 text-[11px] text-slate-400">
                  Used to group the Owner Payout Report and to send branded PDF statements.
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="ns-label mb-2">Channel calendars (Airbnb · Booking.com)</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Airbnb iCal URL</Label>
                  <Input
                    data-testid={PROPERTIES.icalAirbnbInput}
                    className="mt-1 h-10 bg-white text-xs font-mono"
                    placeholder="https://www.airbnb.com/calendar/ical/…"
                    value={form.ical_airbnb_url}
                    onChange={(e) => setForm({ ...form, ical_airbnb_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Booking.com iCal URL</Label>
                  <Input
                    data-testid={PROPERTIES.icalBookingInput}
                    className="mt-1 h-10 bg-white text-xs font-mono"
                    placeholder="https://admin.booking.com/hotel/…"
                    value={form.ical_booking_url}
                    onChange={(e) => setForm({ ...form, ical_booking_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Vrbo iCal URL</Label>
                  <Input
                    className="mt-1 h-10 bg-white text-xs font-mono"
                    placeholder="https://www.vrbo.com/icalendar/…"
                    value={form.ical_vrbo_url}
                    onChange={(e) => setForm({ ...form, ical_vrbo_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Expedia iCal URL</Label>
                  <Input
                    className="mt-1 h-10 bg-white text-xs font-mono"
                    placeholder="https://www.expediapartnercentral.com/…"
                    value={form.ical_expedia_url}
                    onChange={(e) => setForm({ ...form, ical_expedia_url: e.target.value })}
                  />
                </div>
                <div className="text-[11px] text-slate-400">
                  Bookings pulled hourly and shown read-only on the Calendar and Bookings pages.
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <div className="ns-label">Direct booking (beta)</div>
                <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.direct_booking_enabled}
                    onChange={(e) => setForm({ ...form, direct_booking_enabled: e.target.checked })}
                    className="w-3.5 h-3.5 accent-teal-600"
                  />
                  <span>Publish</span>
                </label>
              </div>
              {form.guestbook_slug && form.direct_booking_enabled && (
                <div className="mt-2 flex items-center gap-2 bg-teal-50/40 border border-teal-100 rounded-lg p-2.5">
                  <code className="text-[11px] text-teal-800 truncate flex-1">
                    {window.location.origin}/book/{form.guestbook_slug}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/book/${form.guestbook_slug}`);
                      toast.success("Direct booking link copied");
                    }}
                    className="text-teal-700 hover:text-teal-900"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500">Nightly £</Label>
                  <Input
                    type="number"
                    min={0}
                    className="mt-1 h-10 bg-white"
                    value={form.direct_booking_nightly_rate}
                    onChange={(e) => setForm({ ...form, direct_booking_nightly_rate: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Min nights</Label>
                  <Input
                    type="number"
                    min={1}
                    className="mt-1 h-10 bg-white"
                    value={form.direct_booking_min_nights}
                    onChange={(e) => setForm({ ...form, direct_booking_min_nights: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cleaning £</Label>
                  <Input
                    type="number"
                    min={0}
                    className="mt-1 h-10 bg-white"
                    value={form.direct_booking_cleaning_fee}
                    onChange={(e) => setForm({ ...form, direct_booking_cleaning_fee: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="text-[11px] text-slate-400 mt-2">
                Stripe checkout is being wired up. Guests can request a quote today — payment goes live in Phase 2.
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <div className="ns-label">Guest book (shareable page)</div>
                <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer">
                  <input
                    data-testid={PROPERTIES.guestBookEnabled}
                    type="checkbox"
                    checked={form.guestbook_enabled}
                    onChange={(e) => setForm({ ...form, guestbook_enabled: e.target.checked })}
                    className="w-3.5 h-3.5 accent-teal-600"
                  />
                  <span>Publish</span>
                </label>
              </div>
              {editing && form.guestbook_slug && (
                <div className="mt-2 flex items-center gap-2 bg-teal-50/40 border border-teal-100 rounded-lg p-2.5">
                  <code className="text-[11px] text-teal-800 truncate flex-1">
                    {window.location.origin}/g/{form.guestbook_slug}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/g/${form.guestbook_slug}`);
                      toast.success("Guest book link copied");
                    }}
                    className="text-teal-700 hover:text-teal-900"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500">Wi-Fi network name (SSID)</Label>
                  <Input
                    data-testid={PROPERTIES.guestBookWifiSsid}
                    className="mt-1 h-10 bg-white font-mono"
                    placeholder="NewportStays-Guest"
                    value={form.guestbook_wifi_ssid}
                    onChange={(e) => setForm({ ...form, guestbook_wifi_ssid: e.target.value })}
                  />
                  <div className="text-[11px] text-slate-400 mt-1">
                    The Wi-Fi password already saved above is used automatically.
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Check-out instructions</Label>
                  <Textarea
                    data-testid={PROPERTIES.guestBookCheckout}
                    className="mt-1 bg-white"
                    rows={2}
                    placeholder="Please strip the beds, empty the fridge, and drop keys in the lockbox."
                    value={form.guestbook_checkout}
                    onChange={(e) => setForm({ ...form, guestbook_checkout: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Local recommendations</Label>
                  <Textarea
                    data-testid={PROPERTIES.guestBookRecs}
                    className="mt-1 bg-white"
                    rows={3}
                    placeholder="· The Anchor — Sunday roast next door · Newport Wetlands · Pen y Fan hike (45 min drive)"
                    value={form.guestbook_recommendations}
                    onChange={(e) => setForm({ ...form, guestbook_recommendations: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">House rules</Label>
                  <Textarea
                    data-testid={PROPERTIES.guestBookRules}
                    className="mt-1 bg-white"
                    rows={2}
                    placeholder="No smoking indoors · Quiet hours 10pm–8am · Please recycle in the blue bin"
                    value={form.guestbook_house_rules}
                    onChange={(e) => setForm({ ...form, guestbook_house_rules: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Emergency contact</Label>
                  <Input
                    data-testid={PROPERTIES.guestBookEmergency}
                    className="mt-1 h-10 bg-white"
                    placeholder="Dionne · 07XXX XXXXXX · hello@newportstays.co.uk"
                    value={form.guestbook_emergency_contact}
                    onChange={(e) => setForm({ ...form, guestbook_emergency_contact: e.target.value })}
                  />
                </div>
                {/* Session Z49 — bespoke PDF uploads (guide + rental agreement). */}
                {editing ? (
                  <div className="rounded-lg border border-slate-100 bg-stone-50/60 p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-700" />
                      <div className="text-sm font-medium text-slate-700">Guest documents (PDF)</div>
                    </div>
                    <div className="text-[11px] text-slate-500 -mt-2">
                      Branded property guide + bespoke rental agreement.
                      Shown to the guest on their check-in portal.
                    </div>
                    {["guide", "agreement"].map((kind) => {
                      const doc = documents[kind];
                      const label = kind === "guide" ? "Property guide" : "Rental agreement";
                      return (
                        <div
                          key={kind}
                          data-testid={`properties-doc-${kind}`}
                          className="flex items-center gap-2 flex-wrap"
                        >
                          <div className="min-w-[9rem] text-xs text-slate-600">{label}</div>
                          {doc ? (
                            <div className="flex items-center gap-2 flex-wrap flex-1">
                              <span className="text-[11px] text-slate-500 truncate max-w-[22rem]">
                                {doc.original_filename} · v{doc.version} · {Math.round((doc.size || 0) / 1024)} KB
                              </span>
                              <label
                                className={`inline-flex items-center gap-1 text-[11px] rounded-full border border-slate-200 bg-white text-slate-600 px-2.5 py-1 cursor-pointer hover:border-teal-600 hover:text-teal-700 ${
                                  uploadingDoc === kind ? "opacity-40" : ""
                                }`}
                              >
                                <Upload className="w-3 h-3" />
                                {uploadingDoc === kind ? "Uploading…" : "Replace"}
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  className="hidden"
                                  disabled={uploadingDoc === kind}
                                  onChange={(e) => uploadDocument(kind, e)}
                                  data-testid={`properties-doc-${kind}-input`}
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => removeDocument(kind)}
                                data-testid={`properties-doc-${kind}-remove`}
                                className="text-[11px] text-slate-500 hover:text-red-600 underline"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <label
                              className={`inline-flex items-center gap-1 text-[11px] rounded-full border border-slate-200 bg-white text-slate-600 px-2.5 py-1 cursor-pointer hover:border-teal-600 hover:text-teal-700 ${
                                uploadingDoc === kind ? "opacity-40" : ""
                              }`}
                            >
                              <Upload className="w-3 h-3" />
                              {uploadingDoc === kind ? "Uploading…" : "Upload PDF"}
                              <input
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                disabled={uploadingDoc === kind}
                                onChange={(e) => uploadDocument(kind, e)}
                                data-testid={`properties-doc-${kind}-input`}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 italic">
                    Save this property first — then re-open it to attach a property guide or rental agreement PDF.
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid={PROPERTIES.saveBtn}
              onClick={save}
              className="rounded-full ns-btn-primary px-6"
            >
              {editing ? "Save changes" : "Save property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
